import React, { useState, useMemo, useEffect } from "react";
import {
  X, Star, MapPin, BedDouble, ChevronLeft, ChevronRight, Check,
  Utensils, Ban, ShieldCheck, Wifi, Coffee, Users, Loader2, IdCard,
  Wind, Car, Waves, Dumbbell, ConciergeBell, Sparkles, WashingMachine,
  Baby, Wine, Briefcase, Tv, Landmark, ParkingCircle, CigaretteOff, Info,
  Plane, TrainFront, Train,
} from "lucide-react";
import { formatINR } from "@/mock";
import { apiHotelLandmarks } from "@/api";

const mealLabel = (m) => {
  if (!m) return "Room Only";
  const x = m.toLowerCase();
  if (x.includes("breakfast")) return "Breakfast Included";
  if (x.includes("half")) return "Breakfast + 1 Meal";
  if (x.includes("full")) return "Breakfast + Lunch + Dinner";
  if (x.includes("all")) return "All Meals Included";
  if (x.includes("dinner")) return "Dinner Included";
  return m;
};
const hasMeal = (m) => m && !/room only|bed only/i.test(m);

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

// amenity name -> icon
const amenityIcon = (name) => {
  const n = (name || "").toLowerCase();
  if (n.includes("wifi") || n.includes("wi-fi") || n.includes("internet")) return Wifi;
  if (n.includes("air condition") || n.includes("a/c") || n.includes("ac ")) return Wind;
  if (n.includes("park")) return ParkingCircle;
  if (n.includes("pool") || n.includes("swim")) return Waves;
  if (n.includes("gym") || n.includes("fitness")) return Dumbbell;
  if (n.includes("restaurant") || n.includes("dining")) return Utensils;
  if (n.includes("coffee") || n.includes("cafe") || n.includes("tea")) return Coffee;
  if (n.includes("bar") || n.includes("lounge")) return Wine;
  if (n.includes("laundry") || n.includes("dry clean") || n.includes("washing")) return WashingMachine;
  if (n.includes("concierge") || n.includes("front desk") || n.includes("reception") || n.includes("check-in")) return ConciergeBell;
  if (n.includes("spa") || n.includes("massage") || n.includes("clean")) return Sparkles;
  if (n.includes("business") || n.includes("meeting") || n.includes("conference")) return Briefcase;
  if (n.includes("tv") || n.includes("television")) return Tv;
  if (n.includes("child") || n.includes("baby") || n.includes("kids")) return Baby;
  if (n.includes("car") || n.includes("taxi") || n.includes("shuttle") || n.includes("transfer")) return Car;
  if (n.includes("smoke")) return CigaretteOff;
  if (n.includes("shop") || n.includes("store")) return Landmark;
  return Check;
};

const Gallery = ({ images, name }) => {
  const [idx, setIdx] = useState(0);
  const list = (images || []).filter(Boolean);
  if (!list.length) {
    return (
      <div className="flex h-56 items-center justify-center rounded-xl bg-gradient-to-br from-[#1B1F5B]/10 to-[#F5B32B]/10">
        <BedDouble className="h-12 w-12 text-[#1B1F5B]/30" />
      </div>
    );
  }
  const cur = Math.min(idx, list.length - 1);
  return (
    <div className="grid grid-cols-4 gap-2">
      <div className="group relative col-span-4 h-56 overflow-hidden rounded-xl sm:col-span-3">
        <img src={list[cur]} alt={name} className="h-full w-full object-cover"
          onError={(e) => { e.currentTarget.style.display = "none"; }} />
        {list.length > 1 && (
          <>
            <button onClick={() => setIdx((cur - 1 + list.length) % list.length)}
              className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => setIdx((cur + 1) % list.length)}
              className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
              <ChevronRight className="h-4 w-4" />
            </button>
            <span className="absolute bottom-2 right-2 rounded-md bg-black/55 px-2 py-0.5 text-[11px] font-bold text-white">{cur + 1}/{list.length}</span>
          </>
        )}
      </div>
      <div className="col-span-4 hidden gap-2 sm:col-span-1 sm:flex sm:flex-col">
        {list.slice(0, 3).map((src, i) => (
          <button key={i} onClick={() => setIdx(i)}
            className={`h-[68px] w-full overflow-hidden rounded-lg border-2 ${cur === i ? "border-[#1B1F5B]" : "border-transparent"}`}>
            <img src={src} alt="" className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          </button>
        ))}
      </div>
    </div>
  );
};

export const HotelDetailModal = ({ hotel, nights, options, loading, onBook, onClose }) => {
  const [filters, setFilters] = useState({ breakfast: false, refundable: false });
  const [nearby, setNearby] = useState(null);

  // fetch nearby landmarks/transport once per hotel
  useEffect(() => {
    setNearby(null);
    if (hotel?.lat && hotel?.lng) {
      apiHotelLandmarks(hotel.lat, hotel.lng).then(setNearby).catch(() => {});
    }
  }, [hotel?.hotelId]);
  const list = options?.list || [];
  const filtered = useMemo(() => {
    return list.filter((o) => {
      if (filters.breakfast && !hasMeal(o.mealBasis)) return false;
      if (filters.refundable && !o.freeCancellation) return false;
      return true;
    });
  }, [list, filters]);

  if (!hotel) return null;

  const amenities = hotel.amenities || [];
  const hasGeo = hotel.lat && hotel.lng;
  const mapSrc = hasGeo
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${hotel.lng - 0.01}%2C${hotel.lat - 0.008}%2C${hotel.lng + 0.01}%2C${hotel.lat + 0.008}&layer=mapnik&marker=${hotel.lat}%2C${hotel.lng}`
    : null;

  const toggle = (k) => setFilters((p) => ({ ...p, [k]: !p[k] }));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:p-6" data-testid="hotel-detail-modal">
      <div className="my-4 w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
        {/* header */}
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 rounded-t-2xl border-b border-slate-100 bg-white p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-lg font-extrabold text-slate-800" data-testid="detail-hotel-name">{hotel.name}</h2>
              {hotel.star > 0 && (
                <span className="flex shrink-0 items-center gap-0.5 rounded-md bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-600">
                  {Array.from({ length: hotel.star }).map((_, i) => <Star key={i} className="h-3 w-3 fill-amber-500 text-amber-500" />)}
                </span>
              )}
            </div>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500">
              <MapPin className="h-3 w-3 shrink-0 text-[#F5B32B]" />
              {[hotel.city, hotel.country].filter(Boolean).join(", ")}{hotel.address ? ` · ${hotel.address}` : ""}
            </p>
          </div>
          <button onClick={onClose} data-testid="detail-close" className="shrink-0 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5">
          <Gallery images={hotel.images && hotel.images.length ? hotel.images : (hotel.image ? [hotel.image] : [])} name={hotel.name} />

          {/* About + star rating */}
          {(hotel.description || hotel.star > 0) && (
            <div className="mt-5 rounded-2xl bg-[#F5F7FB] p-4" data-testid="hotel-about">
              <div className="flex items-center justify-between gap-3">
                <h3 className="flex items-center gap-1.5 text-sm font-extrabold text-slate-800"><Info className="h-4 w-4 text-[#1B1F5B]" /> About this hotel</h3>
                {hotel.star > 0 && (
                  <span className="flex items-center gap-1.5 rounded-lg bg-[#1B1F5B] px-2.5 py-1 text-xs font-extrabold text-white">
                    <Star className="h-3.5 w-3.5 fill-[#F5B32B] text-[#F5B32B]" /> {hotel.star}.0 · {hotel.star >= 4 ? "Excellent" : hotel.star === 3 ? "Very Good" : "Good"}
                  </span>
                )}
              </div>
              {hotel.description && <p className="mt-2 text-[12px] leading-relaxed text-slate-600 line-clamp-4">{hotel.description}</p>}
            </div>
          )}

          {/* Amenities */}
          {amenities.length > 0 && (
            <div className="mt-5" data-testid="hotel-amenities">
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Property amenities</h3>
              <div className="mt-3 grid grid-cols-2 gap-2.5 sm:grid-cols-3 md:grid-cols-4">
                {amenities.map((a, i) => {
                  const Icon = amenityIcon(a);
                  return (
                    <div key={i} className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2 text-[12px] font-semibold text-slate-600">
                      <Icon className="h-4 w-4 shrink-0 text-[#1B1F5B]" /> <span className="truncate">{a}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Location + Nearby transport/landmarks */}
          {mapSrc && (
            <div className="mt-5" data-testid="hotel-map">
              <h3 className="flex items-center gap-1.5 text-sm font-extrabold uppercase tracking-wide text-slate-800"><MapPin className="h-4 w-4 text-[#F5B32B]" /> Location &amp; Nearby</h3>
              <div className="mt-3 grid gap-4 md:grid-cols-2">
                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <iframe title="hotel-location" src={mapSrc} className="h-56 w-full" loading="lazy" />
                  <a href={`https://www.openstreetmap.org/?mlat=${hotel.lat}&mlon=${hotel.lng}#map=16/${hotel.lat}/${hotel.lng}`} target="_blank" rel="noreferrer"
                    className="block bg-white px-3 py-2 text-[11px] font-bold text-[#1B1F5B] hover:underline">View larger map →</a>
                </div>
                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-500">Transport &amp; landmarks</p>
                  {!nearby && <p className="mt-3 text-[12px] text-slate-400">Finding nearby places…</p>}
                  {nearby && (
                    <ul className="mt-2.5 space-y-2.5" data-testid="nearby-list">
                      {nearby.airport && (
                        <li className="flex items-center gap-2.5 text-[12px] text-slate-700">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sky-50"><Plane className="h-3.5 w-3.5 text-sky-600" /></span>
                          <span className="flex-1 truncate font-semibold">{nearby.airport.name}</span>
                          <span className="shrink-0 font-bold text-[#1B1F5B]">{nearby.airport.distanceKm} km</span>
                        </li>
                      )}
                      {nearby.railways?.map((r, i) => (
                        <li key={`r${i}`} className="flex items-center gap-2.5 text-[12px] text-slate-700">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-emerald-50"><Train className="h-3.5 w-3.5 text-emerald-600" /></span>
                          <span className="flex-1 truncate font-semibold">{r.name}</span>
                          <span className="shrink-0 font-bold text-[#1B1F5B]">{r.distanceKm} km</span>
                        </li>
                      ))}
                      {nearby.metros?.map((m, i) => (
                        <li key={`m${i}`} className="flex items-center gap-2.5 text-[12px] text-slate-700">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-violet-50"><TrainFront className="h-3.5 w-3.5 text-violet-600" /></span>
                          <span className="flex-1 truncate font-semibold">{m.name} <span className="font-normal text-slate-400">(metro)</span></span>
                          <span className="shrink-0 font-bold text-[#1B1F5B]">{m.distanceKm} km</span>
                        </li>
                      ))}
                      {nearby.landmarks?.map((l, i) => (
                        <li key={`l${i}`} className="flex items-center gap-2.5 text-[12px] text-slate-700">
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-50"><Landmark className="h-3.5 w-3.5 text-amber-600" /></span>
                          <span className="flex-1 truncate font-semibold">{l.name}</span>
                          <span className="shrink-0 font-bold text-[#1B1F5B]">{l.distanceKm} km</span>
                        </li>
                      ))}
                      {!nearby.airport && !nearby.railways?.length && !nearby.metros?.length && !nearby.landmarks?.length && (
                        <li className="text-[12px] text-slate-400">No major transport hubs found nearby.</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Rooms header + filters */}
          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">Select your room</h3>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => toggle("breakfast")} data-testid="filter-breakfast"
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${filters.breakfast ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-emerald-400"}`}>
                <Utensils className="h-3 w-3" /> Breakfast included
              </button>
              <button onClick={() => toggle("refundable")} data-testid="filter-refundable"
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-[11px] font-bold transition-colors ${filters.refundable ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500 hover:border-emerald-400"}`}>
                <ShieldCheck className="h-3 w-3" /> Free cancellation
              </button>
            </div>
          </div>

          {loading && (
            <div className="py-12 text-center">
              <Loader2 className="mx-auto h-8 w-8 animate-spin text-[#1B1F5B]" />
              <p className="mt-2 text-sm font-semibold text-slate-500">Loading live room rates…</p>
            </div>
          )}

          {!loading && list.length === 0 && (
            <p className="py-10 text-center text-sm text-slate-400">No bookable rooms for these dates. Try different dates.</p>
          )}

          {!loading && list.length > 0 && filtered.length === 0 && (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="no-rooms-match">No rooms match these filters. <button onClick={() => setFilters({ breakfast: false, refundable: false })} className="font-bold text-[#1B1F5B] hover:underline">Clear filters</button></p>
          )}

          {!loading && (
            <div className="mt-3 space-y-3">
              {filtered.map((o) => {
                const meal = hasMeal(o.mealBasis);
                return (
                  <div key={o.optionId} data-testid={`room-option-${o.optionId}`}
                    className="grid gap-4 rounded-2xl border border-slate-200 p-4 transition-shadow hover:shadow-md md:grid-cols-[150px_1fr_220px]">
                    <div className="relative h-32 overflow-hidden rounded-xl bg-slate-100 md:h-full">
                      {o.roomImage ? (
                        <img src={o.roomImage} alt={o.roomName || "Room"} loading="lazy"
                          data-testid={`room-image-${o.optionId}`}
                          className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-slate-300"><BedDouble className="h-8 w-8" /></div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <BedDouble className="h-4 w-4 shrink-0 text-[#1B1F5B]" />
                        <p className="font-bold text-slate-800">{o.roomName || "Standard Room"}</p>
                      </div>
                      {o.roomsDetail?.length > 0 && (
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-400">
                          <Users className="h-3 w-3" /> {o.roomsDetail.map((r) => `${r.adults} adult${r.adults > 1 ? "s" : ""}${r.children ? ` + ${r.children} child` : ""}`).join(" · ")}
                        </p>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-bold ${meal ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {meal ? <Utensils className="h-3 w-3" /> : <Coffee className="h-3 w-3" />} {mealLabel(o.mealBasis)}
                        </span>
                        {o.freeCancellation ? (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2.5 py-1 text-[11px] font-bold text-emerald-700"><ShieldCheck className="h-3 w-3" /> {o.freeCancellationUntil ? `Free cancellation till ${fmtDate(o.freeCancellationUntil)}` : "Free Cancellation"}</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-rose-50 px-2.5 py-1 text-[11px] font-bold text-rose-600"><Ban className="h-3 w-3" /> Non-Refundable</span>
                        )}
                        {o.panRequired && (
                          <span className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1 text-[11px] font-bold text-amber-700"><IdCard className="h-3 w-3" /> PAN required</span>
                        )}
                        {o.passportRequired && (
                          <span data-testid={`passport-required-badge-${o.optionId}`} className="inline-flex items-center gap-1 rounded-lg bg-sky-50 px-2.5 py-1 text-[11px] font-bold text-sky-700"><IdCard className="h-3 w-3" /> Passport required</span>
                        )}
                      </div>
                      {(o.inclusions?.length > 0) && (
                        <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                          {o.inclusions.slice(0, 6).map((inc, i) => (
                            <li key={i} className="flex items-center gap-1.5 text-[12px] text-slate-600">
                              <Check className="h-3.5 w-3.5 shrink-0 text-emerald-500" /> <span className="truncate">{inc}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                      {o.bookingNotes && (
                        <p className="mt-2.5 rounded-lg bg-slate-50 px-3 py-2 text-[11px] leading-relaxed text-slate-500 line-clamp-3">{o.bookingNotes}</p>
                      )}
                    </div>

                    <div className="flex flex-col justify-between border-t border-dashed border-slate-200 pt-3 md:border-l md:border-t-0 md:pl-4 md:pt-0">
                      <div className="text-right">
                        {o.strikethrough > 0 && o.strikethrough > o.totalPrice && (
                          <p className="text-[11px] text-slate-400 line-through">{formatINR(o.strikethrough)}</p>
                        )}
                        <p className="text-xl font-extrabold text-[#1B1F5B]">{formatINR(o.totalPrice)}</p>
                        <p className="text-[10px] text-slate-400">for {nights} night{nights > 1 ? "s" : ""} incl. taxes</p>
                        <div className="mt-1 space-y-0.5 text-[10px] text-slate-400">
                          <p>Base {formatINR(o.basePrice)}</p>
                          {o.taxes > 0 && <p>Taxes {formatINR(o.taxes)}</p>}
                          {o.mf > 0 && <p>Mgmt fee {formatINR(o.mf)}</p>}
                          {o.mft > 0 && <p>Mgmt fee tax {formatINR(o.mft)}</p>}
                          {o.discount > 0 && <p className="text-emerald-600">Discount −{formatINR(o.discount)}</p>}
                        </div>
                      </div>
                      <button data-testid={`hotel-book-${o.optionId}`} onClick={() => onBook(hotel, o)}
                        className="mt-3 w-full rounded-xl bg-[#F5B32B] py-2.5 text-sm font-extrabold text-[#0A0F2E] shadow-sm transition-all hover:bg-[#E89A10] hover:shadow-md">
                        BOOK THIS ROOM
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HotelDetailModal;
